import React, { useEffect, useRef, useState } from 'react';
import { WagmiProvider } from 'wagmi';
import {
  abstract, arbitrum, avalanche, base, berachain, bsc, mainnet,
  optimism, polygon, scroll, sei, tron, zksync, zora
} from 'wagmi/chains';
import { getDefaultConfig, RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import BottomNav from './components/BottomNav';
import WalletPage from './pages/WalletPage';
import StakingPage from './pages/StakingPage';
import MiningPage from './pages/MiningPage';
import '@rainbow-me/rainbowkit/styles.css';

const config = getDefaultConfig({
  appName: 'Crypto DApp',
  projectId: '9edeea5b4d4364f1ad3db9cfff462cb9',
  chains: [mainnet, base, arbitrum, avalanche, optimism, zksync, polygon, bsc, scroll, berachain, abstract, zora, tron, sei],
  ssr: false,
});

const queryClient = new QueryClient();

function App() {
  const [selectedTab, setSelectedTab] = useState('wallet');
  const [selectedChainId, setSelectedChainId] = useState(1);
  const [isMining, setIsMining] = useState(false);
  const [logs, setLogs] = useState([]);
  const [totalMined, setTotalMined] = useState(0);
  const workerRef = useRef(null);

  // ✅ Create the worker once
  useEffect(() => {
    if (!workerRef.current && typeof Worker !== "undefined") {
      try {
        workerRef.current = new Worker('/mining-worker.js');
      } catch (err) {
        console.error('Worker init error:', err);
      }
    }
  }, []);

  // ✅ Set mining data + bind worker message handler per chain
  useEffect(() => {
    const chainLogsKey = `logs_${selectedChainId}`;
    const savedLogs = JSON.parse(sessionStorage.getItem(chainLogsKey)) || [];
    const savedTotalMinedKey = `totalMined_${selectedChainId}`;
    const savedTotalMined = parseFloat(sessionStorage.getItem(savedTotalMinedKey)) || 0;

    setLogs(savedLogs);
    setTotalMined(savedTotalMined);

    if (workerRef.current && isMining) {
      workerRef.current.postMessage('stop');
    }
    setIsMining(false);

    if (workerRef.current) {
      workerRef.current.onmessage = (event) => {
        if (event.data.type === 'newLog') {
          const newLog = event.data.log;

          setLogs((prevLogs) => {
            const updatedLogs = [newLog, ...prevLogs.slice(0, 99)];
            sessionStorage.setItem(`logs_${selectedChainId}`, JSON.stringify(updatedLogs));
            return updatedLogs;
          });

          setTotalMined((prev) => {
            const updated = +(prev + newLog.amount).toFixed(6);
            sessionStorage.setItem(`totalMined_${selectedChainId}`, updated.toString());
            return updated;
          });
        }
      };
    }
  }, [selectedChainId]);

  const toggleMining = () => {
    if (workerRef.current) {
      workerRef.current.postMessage(isMining ? 'stop' : 'start');
      setIsMining((prev) => !prev);
    }
  };

  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider>
          <div className="min-h-screen flex flex-col bg-[#0d0d0d] text-white">
            <main className="flex-grow p-0">
              <div className="desktop-segment hidden md:flex justify-center mb-6">
                <div className="bg-[#1a1a1a] p-2 rounded-2xl shadow-lg border border-[#2a2a2a] flex space-x-3">
                  {['wallet', 'staking', 'mining'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setSelectedTab(tab)}
                      className={`px-6 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${selectedTab === tab
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'bg-[#2a2a2a] text-gray-400 hover:bg-[#333333]'
                        }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {selectedTab === 'wallet' && (
                <WalletPage
                  selectedChainId={selectedChainId}
                  setSelectedChainId={setSelectedChainId}
                />
              )}
              {selectedTab === 'staking' && (
                <StakingPage selectedChainId={selectedChainId} />
              )}
              {selectedTab === 'mining' && (
                <MiningPage
                  isMining={isMining}
                  logs={logs}
                  totalMined={totalMined}
                  toggleMining={toggleMining}
                  selectedChainId={selectedChainId}
                />
              )}
            </main>

            <BottomNav setSelectedTab={setSelectedTab} />
          </div>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default App;
